// Task #214 — small in-app tooltip primitive. The codebase previously
// relied on the native ``title`` attribute for hover hints, which
// doesn't satisfy the keyboard-focus / Escape-to-dismiss requirements
// of the Team-performance triage row. This component wraps a single
// focusable trigger and shows a styled popup on hover or focus,
// dismissable with Escape. No new dependencies.
import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";

export interface TooltipProps {
  // Text or richer node shown inside the popup.
  content: ReactNode;
  // The trigger. Must be a single React element so we can wire
  // ``aria-describedby`` onto it for screen-reader exposure.
  children: ReactElement;
  // Test hook for the popup element so tests don't have to rely on
  // matching the (potentially long) copy verbatim.
  "data-testid"?: string;
  // Optional override for the wrapper span. The wrapper must remain
  // ``position: relative`` so the absolutely-positioned popup is
  // anchored correctly, but consumers can swap the default
  // ``inline-flex items-center`` for layout-critical contexts (e.g. a
  // chart bar that needs to be a ``flex-1`` flex item or a segment
  // sized with ``width: x%``). ``relative`` is always prepended.
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
}

type TriggerProps = {
  "aria-describedby"?: string;
  onMouseEnter?: (e: MouseEvent) => void;
  onMouseLeave?: (e: MouseEvent) => void;
  onFocus?: (e: FocusEvent) => void;
  onBlur?: (e: FocusEvent) => void;
  onKeyDown?: (e: KeyboardEvent) => void;
};

export function Tooltip({
  content,
  children,
  "data-testid": testId,
  wrapperClassName,
  wrapperStyle,
}: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  // ``dismissed`` tracks the user pressing Escape — we keep the
  // tooltip closed until the trigger loses focus / hover so a single
  // Escape doesn't immediately re-open as the mouse is still over it.
  const dismissedRef = useRef(false);

  const show = useCallback(() => {
    if (dismissedRef.current) return;
    setOpen(true);
  }, []);
  const hide = useCallback(() => {
    dismissedRef.current = false;
    setOpen(false);
  }, []);

  // Escape closes the tooltip even when the mouse stays over the
  // trigger. Listening on the document mirrors how ``ConfirmDialog``
  // handles its Escape key.
  useEffect(() => {
    if (!open) return;
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        dismissedRef.current = true;
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  if (!isValidElement(children)) return children;

  const childProps = (children as ReactElement<TriggerProps>).props;

  // Compose handlers so consumers can still attach their own focus /
  // mouse handlers on the trigger.
  const triggerProps: TriggerProps = {
    "aria-describedby": open
      ? [childProps["aria-describedby"], id].filter(Boolean).join(" ")
      : childProps["aria-describedby"],
    onMouseEnter: (e: MouseEvent) => {
      childProps.onMouseEnter?.(e);
      show();
    },
    onMouseLeave: (e: MouseEvent) => {
      childProps.onMouseLeave?.(e);
      hide();
    },
    onFocus: (e: FocusEvent) => {
      childProps.onFocus?.(e);
      show();
    },
    onBlur: (e: FocusEvent) => {
      childProps.onBlur?.(e);
      hide();
    },
    onKeyDown: (e: KeyboardEvent) => {
      childProps.onKeyDown?.(e);
      if (e.key === "Escape") {
        dismissedRef.current = true;
        setOpen(false);
      }
    },
  };

  const trigger = cloneElement(
    children as ReactElement<TriggerProps>,
    triggerProps,
  );

  return (
    <span
      className={`relative ${wrapperClassName ?? "inline-flex items-center"}`}
      style={wrapperStyle}
    >
      {trigger}
      <span
        role="tooltip"
        id={id}
        data-testid={testId}
        data-state={open ? "open" : "closed"}
        // ``hidden`` keeps the node out of the accessibility tree when
        // closed but still discoverable via the id for tests that
        // want to query by role. We rely on ``hidden`` + CSS so the
        // popup doesn't take up layout space when collapsed.
        hidden={!open}
        className="pointer-events-none absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 whitespace-normal rounded border border-border bg-popover px-2 py-1 text-xs font-normal text-popover-foreground shadow-md min-w-[12rem] max-w-[18rem] text-left"
      >
        {content}
      </span>
    </span>
  );
}
